import React, { useEffect, useState } from 'react'
import Table from 'react-bootstrap/Table'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { DashboardControlRun } from 'js/models'
import { listControlRuns, ListControlRunsParams } from 'js/services/controlRuns'
import { MONTH_NAMES } from 'js/utils/constants'
import { formatDate } from 'js/utils/helpers'
import Paths from 'js/utils/paths'

import Loader from '../Loader'
import { Button } from 'react-bootstrap'

export default function ControlRunsTable(props: {
  orgId?: number | null
  startMonth?: number
  startYear?: number
  setShowMonthList: (bool: boolean) => void
  updateOrgParam: boolean
}): React.ReactElement {
  const { orgId, startMonth, startYear, setShowMonthList, updateOrgParam } =
    props
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()

  const [awaitingResponse, setAwaitingResponse] = useState(true)
  const [runs, setRuns] = useState<DashboardControlRun[]>()

  useEffect(() => {
    setAwaitingResponse(true)
    if (orgId === undefined) return
    if (startMonth && startYear) {
      listControlRuns({
        ...(orgId && {
          organization: orgId,
        }),
        startMonth: startMonth,
        startYear: startYear,
      })
        .then(setRuns)
        .catch(() => setRuns([]))
        .finally(() => setAwaitingResponse(false))
    }
  }, [orgId, startMonth, startYear])

  const renderTableRow = (
    controlRun: DashboardControlRun,
    i: number
  ): React.ReactElement => {
    const rowLink = Paths.getControlRunDetailLink({
      year: controlRun.datetimeYear,
      month: controlRun.datetimeMonth,
      deviceId: controlRun.device,
      lotGroupId: controlRun.lotGroup,
    })
    return (
      <tr
        key={'control-run-table-row-' + i}
        className='clickable'
        onClick={(): void | Promise<void> => navigate(rowLink)}
      >
        <td>
          {MONTH_NAMES[controlRun.datetimeMonth]} {controlRun.datetimeYear}
        </td>
        <td>{controlRun.test}</td>
        <td>
          {controlRun.label && controlRun.serialNumber
            ? `${controlRun.label} (${controlRun.serialNumber})`
            : '–'}
        </td>
        <td>{controlRun.lots.join(', ')}</td>
        <td>{controlRun.count}</td>
        <td>{formatDate(controlRun.lastEdit)}</td>
      </tr>
    )
  }

  if (!(runs && startMonth && startYear)) return <Loader />

  return (
    <div>
      <Button
        onClick={(): void => {
          setSearchParams({
            ...(orgId && updateOrgParam
              ? { organization: orgId.toString() }
              : {}),
          })
          setShowMonthList(true)
        }}
        variant='secondary'
        size='sm'
        active={false}
        style={{ marginBottom: '1rem' }}
      >
        See All Months with Data
      </Button>
      {awaitingResponse ? (
        <Loader />
      ) : runs.length == 0 ? (
        <p className='m-0'>No control runs for the selected month</p>
      ) : (
        <Table striped hover className='table-header-primary'>
          <thead>
            <tr>
              <th>Month</th>
              <th>Test</th>
              <th>Device</th>
              <th>Lot Numbers</th>
              <th># Data Points</th>
              <th>Last Edited</th>
            </tr>
          </thead>
          <tbody>{runs.map((cr, i) => renderTableRow(cr, i))}</tbody>
        </Table>
      )}
    </div>
  )
}
